import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useHandHistory } from 'hooks/useHandHistory';

import { getCommsPanelStyle } from 'components/CommsPanel/panelGeometry';

import { BoardCards, CardStrip } from './Cards';
import { formatHandDate, formatMetadata, formatPot } from './format';
import { useDemoPlayerCount } from './HandHistoryDemoProvider';
import { HAND_HISTORY_LAYOUTS } from './HandHistoryLayouts';
import { HandHistoryPanel } from './HandHistoryPanel';
import { HandHistoryV1Panel } from './HandHistoryV1Panel';
import { HandSnapshot } from './HandSnapshot';
import { getPotTotal } from './handSummary';
import { PlayByPlayList } from './PlayByPlayList';
import { ActiveHand, HandHistorySnapshot } from './types';

import shared from './HandHistory.module.scss';
import styles from './HandHistoryProposals.module.scss';
import v1 from './HandHistoryV1.module.scss';

const descriptions = {
  A: 'Newest hand first. Expand a hand to see its snapshot, results and actions.',
  B: 'The current hand stays pinned while you browse completed hands below.',
  C: 'Browse hands oldest first, with the latest action first within each hand.',
  D: 'Choose a hand using your cards and the board, then inspect its details below.',
  E: 'Read payouts first, then expand the table snapshot and play-by-play.'
};

// Keep desktop geometry while letting V1's mobile breakpoints remove side gutters.
const desktopPanelStyle = getCommsPanelStyle(false, true, { side: 'left' });
const proposalPanelStyle = {
  ...desktopPanelStyle,
  left: `var(--proposal-panel-left, ${desktopPanelStyle.left})`,
  right: 'var(--proposal-panel-right, auto)',
  width: `var(--proposal-panel-width, ${desktopPanelStyle.width})`
};

const Previews = ({
  cards,
  board,
  boards
}: {
  cards?: string[];
  board: string[];
  boards?: string[][];
}) => (
  <div className={styles.previews}>
    <div className={styles.preview}>
      <span>Your cards</span>
      <CardStrip cards={cards} revealed emptyText='Not available' />
    </div>
    <div className={styles.preview}>
      <span>Board</span>
      {(boards && boards.length > 1 ? boards : [board]).map((runout, index) => (
        <React.Fragment key={index}>
          {boards && boards.length > 1 && <span>Run {index + 1}</span>}
          <BoardCards cards={runout} />
        </React.Fragment>
      ))}
    </div>
  </div>
);

const ownCards = (hand: HandHistorySnapshot) =>
  hand.tableSnapshot.seats.find((seat) => seat.seat === hand.tableSnapshot.viewerSeat)?.cards;

// Synthetic active-hand cards are independent of the previous completed hand.
// The live ActiveHand contract is deliberately unchanged by this playground.
const activeCards = (hand: ActiveHand) =>
  ['As', 'Kh', 'Qc', 'Jd', '10s', '9h', '8c', '7d', '6s']
    .filter((card) => !hand.board.includes(card))
    .slice(0, hand.gameVariant === 'omaha' ? 4 : 2);

const Metadata = ({ hand }: { hand: HandHistorySnapshot }) => (
  <>
    <section className={v1.section}>
      <div className={v1.sectionHeader}>
        <span>Game No</span>
        <strong>
          {hand.tableId || hand.gameId}-{hand.gameNo}
        </strong>
      </div>
    </section>
    <section className={v1.section}>
      <div className={v1.sectionHeader}>
        <span>Date</span>
        <strong>{formatHandDate(hand.completedAt)}</strong>
      </div>
    </section>
  </>
);

const HandSummary = ({ hand }: { hand: HandHistorySnapshot }) => (
  <>
    <strong>Hand #{hand.gameNo}</strong>
    <span className={styles.status}>Completed</span>
    <small>
      {formatHandDate(hand.completedAt)} · Pot {formatPot(getPotTotal(hand), hand.gameType)}
    </small>
    <Previews cards={ownCards(hand)} board={hand.board} boards={hand.boards} />
  </>
);

const ActiveSummary = ({ hand }: { hand: ActiveHand }) => (
  <>
    <strong>Hand #{hand.gameNo}</strong>
    <span className={styles.status}>In progress</span>
    <small>{formatMetadata(hand.metadata, hand.gameType)}</small>
    <Previews cards={activeCards(hand)} board={hand.board} />
  </>
);

const ActiveLog = ({ hand }: { hand: ActiveHand }) => (
  <div className={styles.active}>
    <div className={v1.playByPlayToggleStatic}>
      <span>Play-by-play</span>
      <strong>{hand.playByPlay.length} events</strong>
    </div>
    <div className={styles.actionLog}>
      <PlayByPlayList events={hand.playByPlay} gameType={hand.gameType} showTableCards />
    </div>
  </div>
);

const CompletedCard = ({
  hand,
  expanded,
  resultsFirst,
  chronological,
  onSelect
}: {
  hand: HandHistorySnapshot;
  expanded: boolean;
  resultsFirst?: boolean;
  chronological?: boolean;
  onSelect: () => void;
}) => (
  <article
    className={chronological ? styles.timeline : undefined}
    data-selected-hand={expanded || undefined}>
    {chronological && (
      <div className={styles.actionLog}>
        <PlayByPlayList events={hand.playByPlay} gameType={hand.gameType} showTableCards />
      </div>
    )}
    <details className={styles.card} open={expanded}>
      <summary
        className={styles.summary}
        // Disclosure follows the context selection: one hand open at a time.
        onClick={(event) => {
          event.preventDefault();
          onSelect();
        }}>
        <HandSummary hand={hand} />
      </summary>
      <Metadata hand={hand} />
      <HandSnapshot hand={hand} presentation={resultsFirst ? 'results-first' : 'embedded'} />
    </details>
  </article>
);

const ProposalPanel = () => {
  const { layout } = useDemoPlayerCount();
  const { data, isOpen, setIsOpen, selectedIndex, setSelectedIndex } = useHandHistory();
  const [showActive, setShowActive] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  useEffect(() => setShowActive(false), [selectedIndex, data.completedHands]);
  useEffect(() => {
    panelRef.current
      ?.querySelector('[data-selected-hand="true"]')
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [layout, selectedIndex, isOpen]);
  if (!isOpen || layout === 'current' || layout === 'v1') return null;
  const active = data.activeHand;
  const selected = data.completedHands[selectedIndex];
  const completed = [...data.completedHands].sort((a, b) =>
    layout === 'C' ? a.completedAt - b.completedAt : b.completedAt - a.completedAt
  );
  const activeCard = active && (
    <details className={styles.card} open={layout !== 'E'}>
      <summary className={styles.summary}>
        <ActiveSummary hand={active} />
      </summary>
      <ActiveLog hand={active} />
    </details>
  );
  return (
    <aside
      ref={panelRef}
      className={`${v1.panel} ${v1.panelOpen} ${styles.theme}`}
      style={proposalPanelStyle}
      aria-label='Hand History'
      onKeyDown={(event) => {
        if (event.key === 'Escape') setIsOpen(false);
      }}>
      <header className={v1.panelHeader}>
        <div>
          <h1>Hand History</h1>
          <small>{HAND_HISTORY_LAYOUTS[layout]}</small>
        </div>
        <button
          type='button'
          className={v1.iconButton}
          aria-label='Close'
          onClick={() => setIsOpen(false)}>
          <CloseIcon />
        </button>
      </header>
      <p className={styles.description}>{descriptions[layout]}</p>
      {data.locked ? (
        <div className={shared.lockedState}>
          <h2>
            {data.lockedReason === 'NOT_DEALT_IN'
              ? 'Be dealt in to unlock hand history for this session.'
              : 'Post your blind to unlock hand history for this session.'}
          </h2>
          <p>History starts from the hand that makes you eligible.</p>
        </div>
      ) : (
        <>
          {layout === 'B' && active && (
            <div className={styles.pinned}>
              <ActiveSummary hand={active} />
              <details>
                <summary>Current play-by-play · {active.playByPlay.length} events</summary>
                <ActiveLog hand={active} />
              </details>
            </div>
          )}
          {layout === 'D' && (
            <nav className={styles.navigator} aria-label='Hand navigator'>
              {active && (
                <button
                  type='button'
                  className={`${styles.picker} ${showActive ? styles.selected : ''}`}
                  aria-pressed={showActive}
                  onClick={() => setShowActive(true)}>
                  <ActiveSummary hand={active} />
                </button>
              )}
              {data.completedHands.map((hand, index) => (
                <button
                  type='button'
                  key={hand.handId}
                  className={`${styles.picker} ${
                    !showActive && selectedIndex === index ? styles.selected : ''
                  }`}
                  aria-pressed={!showActive && selectedIndex === index}
                  onClick={() => {
                    setSelectedIndex(index);
                    setShowActive(false);
                  }}>
                  <HandSummary hand={hand} />
                </button>
              ))}
            </nav>
          )}
          <div className={styles.scroll} key={`${layout}-${selected?.handId}`}>
            {layout === 'D' ? (
              showActive && active ? (
                <ActiveLog hand={active} />
              ) : (
                selected && (
                  <>
                    <Metadata hand={selected} />
                    <HandSnapshot hand={selected} presentation='embedded' />
                  </>
                )
              )
            ) : (
              <>
                {(layout === 'A' || layout === 'E') && activeCard}
                {completed.map((hand) => (
                  <CompletedCard
                    key={hand.handId}
                    hand={hand}
                    expanded={hand.handId === selected?.handId}
                    resultsFirst={layout === 'E'}
                    chronological={layout === 'C'}
                    onSelect={() =>
                      setSelectedIndex(
                        data.completedHands.findIndex(({ handId }) => handId === hand.handId)
                      )
                    }
                  />
                ))}
                {layout === 'C' && activeCard}
              </>
            )}
            {!completed.length && <div className={shared.emptyState}>No concluded hands yet.</div>}
          </div>
        </>
      )}
    </aside>
  );
};

/** Playground only: the game route still renders HandHistoryPanel directly. */
export const HandHistoryPlaygroundPanel = () => {
  const { layout } = useDemoPlayerCount();
  if (layout === 'v1') return <HandHistoryV1Panel />;
  return layout === 'current' ? <HandHistoryPanel /> : <ProposalPanel key={layout} />;
};
